import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useLocation } from 'react-router-dom';

const slideDown = keyframes`
  from {
    transform: translateY(-100%);
  }
  to {
    transform: translateY(0);
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-100%);
  }
`;

const flash = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

const BannerContainer = styled.div`
  position: fixed;
  top: 27px;
  left: 0;
  right: 0;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.3rem 0;
  text-align: center;
  z-index: 0;
  transform: translateY(-100%);
  animation: ${props => {
    if (props['data-show'] === 'true') return slideDown;
    if (props['data-hide'] === 'true') return slideUp;
    return 'none';
  }} 0.5s ease forwards;
  margin-top: 120px;

  @media (max-width: 768px) {
    margin-top: 100px;
    padding: 0.2rem 0;
  }
`;

const UrgentBannerContainer = styled(BannerContainer)`
  background-color: ${props => props.theme.colors.accent};
  color: white;
`;

const SoldOutBannerContainer = styled(BannerContainer)`
  background-color: #ff4444;
  color: white;
`;

const BannerContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.1rem;
  font-weight: 500;
  animation: ${props => props['data-flash'] === 'true' ? flash : 'none'} 0.5s ease 3;
`;

const BannerLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-weight: 600;
  padding: 0.1rem 0.6rem;
  border: 1px solid white;
  border-radius: 15px;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    background-color: white;
    color: ${props => props.theme.colors.accent};
  }
`;

const AnnouncementBanner = () => {
  const [show, setShow] = useState(false);
  const [hide, setHide] = useState(false);
  const [flash, setFlash] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);
  const location = useLocation();
  const isEventsPage = location.pathname.includes('/whats-on/events');
  const isWhatsOnPage = location.pathname === '/whats-on';
  const isEventDetailPage = location.pathname.includes('/event/');
  const isDayTicketsPage = location.pathname.includes('/day-tickets');
  const showCircusMessages = isEventsPage || isWhatsOnPage || isEventDetailPage;

  // Check if current date is after April 21st, 2025
  const isAfterApril21st = () => {
    const cutoffDate = new Date('2025-05-20');
    const currentDate = new Date();
    return currentDate > cutoffDate;
  };

  const circusMessages = [
    {
      text: "10% Off when booking online!",
      type: "urgent",
      duration: 5000
    }
  ];

  const membershipMessage = {
    text: "Explore Unlimited Access & Great Offers",
    type: "urgent",
    duration: 5000
  };

  useEffect(() => {
    // Don't show any banners if after April 21st, 2025
    if (isAfterApril21st()) {
      return;
    }

    if (isDayTicketsPage) {
      // Show membership message on Day Tickets page
      const showTimer = setTimeout(() => {
        setShow(true);
        setHide(false);
        setFlash(true);
        
        const hideTimer = setTimeout(() => {
          setHide(true);
          setShow(false);
        }, 15000);

        return () => clearTimeout(hideTimer);
      }, 3000);

      return () => clearTimeout(showTimer);
    } else if (!showCircusMessages) {
      // Original banner behavior for non-events pages
      const showTimer = setTimeout(() => {
        setShow(true);
        setHide(false);
        setFlash(true);
        
        const hideTimer = setTimeout(() => {
          setHide(true);
          setShow(false);
        }, 15000);

        return () => clearTimeout(hideTimer);
      }, 3000);

      return () => clearTimeout(showTimer);
    } else {
      // Circus event banner sequence
      const showNextMessage = () => {
        setShow(true);
        setHide(false);
        setFlash(true);
        
        const hideTimer = setTimeout(() => {
          setHide(true);
          setShow(false);
          
          const nextMessageTimer = setTimeout(() => {
            setMessageIndex((prev) => (prev + 1) % circusMessages.length);
            setShow(true);
            setHide(false);
            setFlash(true);
          }, 3000);
          
          return () => clearTimeout(nextMessageTimer);
        }, circusMessages[messageIndex].duration);
        
        return () => clearTimeout(hideTimer);
      };

      const initialTimer = setTimeout(showNextMessage, 3000);
      return () => clearTimeout(initialTimer);
    }
  }, [showCircusMessages, messageIndex, isDayTicketsPage]);

  const getBannerContainer = () => {
    if (!showCircusMessages && !isDayTicketsPage) {
      return BannerContainer;
    }
    return UrgentBannerContainer;
  };

  const BannerComponent = getBannerContainer();

  return (
    <BannerComponent data-show={show.toString()} data-hide={hide.toString()}>
      <BannerContent data-flash={flash.toString()}>
        {isDayTicketsPage 
          ? membershipMessage.text
          : showCircusMessages 
            ? circusMessages[messageIndex].text
            : "Don't Miss the 1st Art Collection Tour – 23rd May"}
        {isDayTicketsPage ? (
          <BannerLink to="/memberships">Explore</BannerLink>
        ) : !showCircusMessages && (
          <BannerLink to="/whats-on/events">Book Now</BannerLink>
        )}
      </BannerContent>
    </BannerComponent>
  );
};

export default AnnouncementBanner; 